/**
 * Payload-guard for outbound analytics events (tutor-70k.8).
 *
 * Mirrors `api/src/revisionlab_api/analytics/payload_guard.py` —
 * same whitelist + denylist semantics. The denylist exists as
 * defence in depth alongside the whitelist: a property accidentally
 * named `email` is rejected even if the whitelist is later extended
 * incorrectly.
 *
 * `assertSafe` THROWS on a bad payload. The thin Statsig wrapper
 * catches and downgrades to a console.warn — analytics must never
 * crash the SPA.
 */

import { ALL_EVENTS, ALLOWED_PROPERTY_KEYS } from "@shared/analytics/events";

// Defence-in-depth deny list. Mirrors BANNED_KEYS in the Python guard,
// key for key — `payloadGuard.test.ts` parses that file and fails if the
// two sets differ. These keys never appear in legitimate event payloads and are
// common names for PII or model-generated text.
export const BANNED_KEYS: ReadonlySet<string> = new Set([
  "answer",
  "answer_text",
  "question",
  "question_text",
  "grade",
  "grade_text",
  "grade_feedback",
  "grade_rationale",
  "feedback",
  "rationale",
  "mark_scheme",
  "comment",
  "email",
  "user_email",
  "ip",
  "ip_address",
  "name",
  "full_name",
  // Contract A19. `users.first_name` exists (A9), so `first_name` is
  // banned alongside `name` and `full_name`. `last_name`, `given_name` and
  // `family_name` are banned defensively even though we do not collect them, so
  // a future field cannot arrive unguarded.
  "first_name",
  "last_name",
  "given_name",
  "family_name",
  "phone",
  // PE-9.5 — focus reflection and goal free text are local-only. They
  // have no whitelisted key, and are banned outright so a future
  // whitelist mistake still cannot leak them.
  "reflection",
  "reflection_text",
  "goal_text",
]);

const ALLOWED = new Set<string>(ALLOWED_PROPERTY_KEYS);
const ALL = new Set<string>(ALL_EVENTS);

export class AnalyticsPayloadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AnalyticsPayloadError";
  }
}

export function assertSafe(
  eventName: string,
  properties?: Record<string, unknown>,
): void {
  if (!ALL.has(eventName)) {
    throw new AnalyticsPayloadError(
      `Unknown analytics event "${eventName}" — add it to shared/analytics/events.{py,ts} first.`,
    );
  }
  if (!properties) return;
  for (const [key, value] of Object.entries(properties)) {
    if (BANNED_KEYS.has(key)) {
      throw new AnalyticsPayloadError(
        `Property key "${key}" is on the analytics deny list (carries PII or tutor-generated text).`,
      );
    }
    if (!ALLOWED.has(key)) {
      throw new AnalyticsPayloadError(
        `Property key "${key}" is not in the analytics whitelist — extend ALLOWED_PROPERTY_KEYS only after a privacy review (tutor-70k.8).`,
      );
    }
    const t = typeof value;
    if (t !== "string" && t !== "number" && t !== "boolean") {
      throw new AnalyticsPayloadError(
        `Property "${key}" must be a primitive (string/number/boolean); got ${t}.`,
      );
    }
  }
}
